import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Send, Info } from "lucide-react";
import { EmailTemplate } from "@/types/Email";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EmailTemplateFormProps {
  templateType: "welcome" | "reset_password" | "verification" | "test";
}

export function EmailTemplateForm({ templateType }: EmailTemplateFormProps) {
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [testVariables, setTestVariables] = useState({
    naam: "Test Gebruiker",
    resetLink: "https://turnlesplanner.nl/reset-test",
    verificationLink: "https://turnlesplanner.nl/verify-test"
  });
  const { toast } = useToast();
  
  useEffect(() => {
    async function fetchTemplate() {
      setIsLoading(true);
      try {
        // Typecasting voor Supabase query
        const { data, error } = await supabase
          .from('email_templates')
          .select("*")
          .eq("type", templateType)
          .single();
        
        if (error) throw error;
        setTemplate(data as EmailTemplate);
      } catch (error) {
        console.error("Error fetching template:", error);
        toast({
          title: "Fout bij laden",
          description: "Kon de email template niet laden.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchTemplate();
  }, [templateType, toast]);

  const handleUpdate = async (field: string, value: string | boolean) => {
    if (!template) return;
    
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({ [field]: value })
        .eq("id", template.id);
      
      if (error) throw error;
      
      setTemplate({ ...template, [field]: value });
      
      toast({
        title: "Template bijgewerkt",
        description: "De email template is succesvol bijgewerkt.",
      });
    } catch (error) {
      console.error("Update error:", error);
      toast({
        title: "Fout bij bijwerken",
        description: "Er is een fout opgetreden bij het bijwerken van de template.",
        variant: "destructive",
      });
    }
  };

  const handleVariableChange = (field: string, value: string) => {
    setTestVariables({
      ...testVariables,
      [field]: value
    });
  };

  const replacePlaceholders = (content: string) => {
    return content
      .replace(/{{naam}}/g, testVariables.naam)
      .replace(/{{resetLink}}/g, testVariables.resetLink)
      .replace(/{{verificationLink}}/g, testVariables.verificationLink);
  };

  const sendTestEmail = async () => {
    if (!template || !testEmail || !validateEmail(testEmail)) {
      toast({
        title: "Ongeldig e-mailadres",
        description: "Voer een geldig e-mailadres in om een test e-mail te versturen.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      // Replace placeholders with test values
      const html = replacePlaceholders(template.html_content);
      const text = replacePlaceholders(template.text_content);

      const emailService = template.email_service || "resend";
      
      if (emailService === "resend" || templateType === "reset_password" || templateType === "verification") {
        // System emails or when explicitly using Resend
        const { data, error } = await supabase.functions.invoke("send-email", {
          body: {
            to: testEmail,
            subject: template.subject,
            html,
            text
          },
        });

        if (error) throw error;
        console.log("Email result:", data);
      } else {
        // Marketing emails (welcome) or when explicitly using ActiveCampaign
        let tags: string[] = [];
        if (template.tags) {
          tags = template.tags.split(',').map(tag => tag.trim());
        }

        const { data, error } = await supabase.functions.invoke("send-activecampaign-email", {
          body: {
            to: testEmail,
            subject: template.subject,
            html,
            text,
            list_id: template.list_id ? parseInt(template.list_id) : undefined,
            tags: tags.length > 0 ? tags : undefined
          },
        });

        if (error) throw error;
        console.log("ActiveCampaign email result:", data);
      }

      toast({
        title: "Test e-mail verzonden",
        description: `Er is een test e-mail verzonden naar ${testEmail} via ${emailService === "activecampaign" ? "ActiveCampaign" : "Resend"}`,
      });
    } catch (error) {
      console.error("Error sending test email:", error);
      toast({
        title: "Fout bij verzenden",
        description: "Er is een fout opgetreden bij het verzenden van de test e-mail.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  if (isLoading) {
    return <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (!template) {
    return <div className="p-6">Template niet gevonden</div>;
  }

  const isTransactional = templateType === "reset_password" || templateType === "verification";

  const getPlaceholderInfo = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p>Beschikbare placeholders:</p>
            <ul className="list-disc pl-4 mt-1">
              <li><code>{'{{naam}}'}</code> - Naam van de gebruiker</li>
              <li><code>{'{{resetLink}}'}</code> - Link voor wachtwoordreset</li>
              <li><code>{'{{verificationLink}}'}</code> - Link voor e-mail verificatie</li>
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="subject">Onderwerp</Label>
        <Input
          id="subject"
          value={template.subject}
          onChange={(e) => handleUpdate("subject", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="html_content" className="mr-1">HTML Content</Label>
          {getPlaceholderInfo()}
        </div>
        <Textarea
          id="html_content"
          value={template.html_content}
          onChange={(e) => handleUpdate("html_content", e.target.value)}
          className="min-h-[300px] font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="text_content" className="mr-1">Tekst Content (fallback)</Label>
          {getPlaceholderInfo()}
        </div>
        <Textarea
          id="text_content"
          value={template.text_content}
          onChange={(e) => handleUpdate("text_content", e.target.value)}
          className="min-h-[150px] font-mono text-sm"
        />
      </div>

      {!isTransactional && (
        <>
          <div className="space-y-2">
            <Label htmlFor="email_service">Email Service</Label>
            <Select
              value={template.email_service || "resend"}
              onValueChange={(value) => handleUpdate("email_service", value as "resend" | "activecampaign")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Kies een email service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resend">Resend (System Emails)</SelectItem>
                <SelectItem value="activecampaign">ActiveCampaign (Marketing)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {template.email_service === "activecampaign" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="list_id">ActiveCampaign List ID (optioneel)</Label>
                <Input
                  id="list_id"
                  value={template.list_id || ""}
                  onChange={(e) => handleUpdate("list_id", e.target.value)}
                  placeholder="Bijv. 1"
                />
                <p className="text-sm text-muted-foreground">
                  ID van de ActiveCampaign lijst waartoe ontvangers worden toegevoegd
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">ActiveCampaign Tags (optioneel)</Label>
                <Input
                  id="tags"
                  value={template.tags || ""}
                  onChange={(e) => handleUpdate("tags", e.target.value)}
                  placeholder="Bijv. welkom,nieuw,app"
                />
                <p className="text-sm text-muted-foreground">
                  Comma-gescheiden tags om aan ontvangers toe te voegen
                </p>
              </div>
            </>
          )}
        </>
      )}

      {isTransactional && (
        <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
          <p className="text-sm text-amber-800">
            Dit is een systeem e-mail die altijd via Resend wordt verzonden voor betrouwbaarheid.
          </p>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Switch
          id="is_active"
          checked={template.is_active}
          onCheckedChange={(checked) => handleUpdate("is_active", checked)}
        />
        <Label htmlFor="is_active">Actief</Label>
      </div>

      {/* Test variabelen */}
      <div className="pt-4 mt-4 border-t">
        <h3 className="text-sm font-medium mb-2">Test Variabelen</h3>
        <div className="space-y-3">
          <div>
            <Label htmlFor="test-name">Naam</Label>
            <Input
              id="test-name"
              value={testVariables.naam}
              onChange={(e) => handleVariableChange("naam", e.target.value)}
              placeholder="Test Gebruiker"
            />
          </div>
          <div>
            <Label htmlFor="test-reset-link">Reset Link</Label>
            <Input
              id="test-reset-link"
              value={testVariables.resetLink}
              onChange={(e) => handleVariableChange("resetLink", e.target.value)}
              placeholder="https://example.com/reset"
            />
          </div>
          <div>
            <Label htmlFor="test-verification-link">Verificatie Link</Label>
            <Input
              id="test-verification-link"
              value={testVariables.verificationLink}
              onChange={(e) => handleVariableChange("verificationLink", e.target.value)}
              placeholder="https://example.com/verify"
            />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t">
        <h3 className="text-sm font-medium mb-2">Test Email Versturen</h3>
        <div className="flex space-x-2">
          <Input
            type="email"
            placeholder="email@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <Button 
            onClick={sendTestEmail} 
            disabled={isSending || !testEmail}
            className="whitespace-nowrap"
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Versturen...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Test Versturen
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Via domein: <span className="font-medium">send.turnlesplanner.nl</span>
        </p>
      </div>
    </div>
  );
}
